
import React from 'react';
import { Building2, Home, Shield, Eye, CheckCircle, ExternalLink } from 'lucide-react';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ServiceDetails = () => {
  const commercialServices = [
    {
      title: "TPO, EPDM & Modified Bitumen Roofing",
      description: "Reflective, weldable membranes from trusted brands like GAF EverGuard® and Firestone Building Products"
    },
    {
      title: "Built-Up Roofing (BUR)",
      description: "With gravel ballast and hot-mopped durability"
    },
    {
      title: "Metal Systems",
      description: "For long-span warehouses, shopping centers, and industrial sites"
    },
    {
      title: "Cool Roof Coatings",
      description: "Using Ames® elastomeric technology"
    }
  ];

  const residentialServices = [
    {
      title: "Architectural Shingles",
      description: "From GAF Timberline HDZ® and IKO Dynasty®"
    },
    {
      title: "Luxury & Designer Shingles",
      description: "Including Atlas Pinnacle® Pristine and Owens Corning Duration® Series"
    },
    {
      title: "Metal Roofing",
      description: "In standing seam or metal shingle formats"
    },
    {
      title: "Slate, Clay & Concrete Tile Roofing",
      description: "Designed to last a lifetime"
    }
  ];

  const stormServices = [
    "24/7 Emergency Tarping and Leak Sealing",
    "Insurance Claim Documentation and Estimates",
    "FORTIFIED Roof™ Installation",
    "Drone & Moisture Scan Reports for adjusters"
  ];

  const technologyServices = [
    {
      title: "Drone Inspections",
      description: "With FLIR thermal overlays"
    },
    {
      title: "3D Roof Modeling",
      description: "From EagleView® and Hover"
    },
    {
      title: "Virtual Consultations",
      description: "For remote clients or storm prep planning"
    },
    {
      title: "Energy & Moisture Analytics",
      description: "For home resale or solar compatibility"
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <Accordion type="single" collapsible className="w-full max-w-4xl mx-auto">
          {/* Commercial Roofing */}
          <AccordionItem value="commercial">
            <AccordionTrigger className="text-left">
              <div className="flex items-center">
                <Building2 className="h-6 w-6 text-primary mr-3" />
                <div>
                  <h3 className="text-xl font-bold">🏢 Commercial Roofing Systems</h3>
                  <p className="text-sm text-gray-600">Flat and Low-Slope Expertise, Built to Code</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pt-4">
                <p className="text-gray-700">
                  We install industry-standard and energy-efficient roofing systems designed to meet state and local commercial codes and industry best practices.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {commercialServices.map((service, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold mb-2">{service.title}</h4>
                      <p className="text-sm text-gray-600">{service.description}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-primary">
                  <p className="text-sm">
                    📊 According to the <a href="https://www.nrca.net" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">NRCA</a>, preventive commercial roofing can extend system lifespan by 10–25 years.
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Residential Roofing */}
          <AccordionItem value="residential">
            <AccordionTrigger className="text-left">
              <div className="flex items-center">
                <Home className="h-6 w-6 text-primary mr-3" />
                <div>
                  <h3 className="text-xl font-bold">🏡 Residential Roofing Services</h3>
                  <p className="text-sm text-gray-600">Weather-Resistant Systems, Energy-Conscious Installation</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pt-4">
                <p className="text-gray-700">
                  From shingle upgrades to custom tile roofs, we tailor every roof to regional weather and aesthetic expectations.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {residentialServices.map((service, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold mb-2">{service.title}</h4>
                      <p className="text-sm text-gray-600">{service.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Storm Damage Services */}
          <AccordionItem value="storm">
            <AccordionTrigger className="text-left">
              <div className="flex items-center">
                <Shield className="h-6 w-6 text-primary mr-3" />
                <div>
                  <h3 className="text-xl font-bold">🌪️ Storm Damage Services</h3>
                  <p className="text-sm text-gray-600">Rapid Response | Insurance Support | Storm-Hardened Repairs</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pt-4">
                <p className="text-gray-700">
                  When hurricanes, hail, or high winds hit, we respond with speed and precision. We work directly with all major insurance carriers and follow <a href="https://fortifiedhome.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">FORTIFIED Roof™</a> standards for maximum storm resistance.
                </p>
                <ul className="space-y-2">
                  {stormServices.map((service, index) => (
                    <li key={index} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Technology Services */}
          <AccordionItem value="technology">
            <AccordionTrigger className="text-left">
              <div className="flex items-center">
                <Eye className="h-6 w-6 text-primary mr-3" />
                <div>
                  <h3 className="text-xl font-bold">🛰️ Roofing Technology & Inspection Services</h3>
                  <p className="text-sm text-gray-600">Modern Diagnostics | Safer Evaluations | Data-Backed Decisions</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pt-4">
                <p className="text-gray-700">
                  We use best-in-class assessment tech to analyze your roof without stepping foot on it.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {technologyServices.map((service, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold mb-2">{service.title}</h4>
                      <p className="text-sm text-gray-600">{service.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>
  );
};

export default ServiceDetails;
